import { Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { REGISTER_URL } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const TIERS = [
  {
    name: "Free",
    price: "£0",
    cadence: "forever",
    blurb: "Try the full workflow on a single project.",
    features: [
      "1 active project",
      "Blueprint take-off tools",
      "Estimating & pricing",
      "Applications for payment",
    ],
    featured: false,
    cta: "Start free",
  },
  {
    name: "Growth",
    price: "£29",
    cadence: "per month",
    blurb: "For busy estimators running several jobs at once.",
    features: [
      "Up to 5 active projects",
      "Everything in Free",
      "Progress billing registers",
      "CSV / PDF exports",
    ],
    featured: true,
    cta: "Start free",
  },
  {
    name: "Scale",
    price: "£79",
    cadence: "per month",
    blurb: "Unlimited projects for growing firms.",
    features: [
      "Unlimited active projects",
      "Everything in Growth",
      "Priority support",
      "Full estimate history",
    ],
    featured: false,
    cta: "Start free",
  },
];

export const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 md:py-32 bg-slate-50 border-y border-slate-100"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-blue">
            Trade Off pricing
          </p>
          <h2 className="mt-4 font-heading font-700 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-navy">
            Simple pricing that scales with your projects
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Every plan includes the full take-off to billing toolset. The only
            thing that changes is how many active projects you can run. Prices
            in GBP, excluding VAT.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                className={`flex h-full flex-col rounded-2xl border p-8 transition-all duration-300 ${
                  tier.featured
                    ? "bg-brand-navy border-brand-navy text-white shadow-xl md:-translate-y-2"
                    : "bg-white border-slate-200 hover:shadow-md hover:-translate-y-1"
                }`}
              >
                {tier.featured && (
                  <span className="self-start mb-4 rounded-full bg-brand-sky/20 px-3 py-1 text-xs font-semibold tracking-wide uppercase text-brand-sky">
                    Most popular
                  </span>
                )}
                <h3
                  className={`font-heading font-700 text-2xl ${
                    tier.featured ? "text-white" : "text-brand-navy"
                  }`}
                >
                  {tier.name}
                </h3>
                <div className="mt-4 flex items-end gap-2">
                  <span
                    className={`font-heading font-800 text-4xl ${
                      tier.featured ? "text-white" : "text-brand-navy"
                    }`}
                  >
                    {tier.price}
                  </span>
                  <span
                    className={`mb-1 text-sm ${
                      tier.featured ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {tier.cadence}
                  </span>
                </div>
                <p
                  className={`mt-3 text-sm leading-relaxed ${
                    tier.featured ? "text-slate-300" : "text-slate-600"
                  }`}
                >
                  {tier.blurb}
                </p>

                <ul className="mt-6 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check
                        className={`mt-0.5 h-5 w-5 shrink-0 ${
                          tier.featured ? "text-brand-sky" : "text-brand-blue"
                        }`}
                        strokeWidth={2.5}
                      />
                      <span
                        className={`text-sm ${
                          tier.featured ? "text-slate-200" : "text-slate-700"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  href={REGISTER_URL}
                  className={`mt-8 inline-flex items-center justify-center gap-2 font-medium px-6 py-3 rounded-lg transition-colors ${
                    tier.featured
                      ? "bg-white text-brand-navy hover:bg-slate-100"
                      : "bg-brand-navy text-white hover:bg-brand-navy-dark"
                  }`}
                >
                  {tier.cta}
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 text-center">
            <p className="text-slate-600">
              Not sure which plan fits, or need something bespoke?{" "}
              <button
                data-testid="pricing-talk-to-us"
                onClick={() => scrollToSection("contact")}
                className="font-semibold text-brand-blue hover:underline"
              >
                Talk to us
              </button>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
